/*
 * Toro cart: drawer + cart page behaviour.
 * - Opens the drawer from the header cart icon and after any add to cart (product page, product cards).
 * - Quantity, remove and discount codes go through Shopify's Ajax Cart API, then the cart markup is re-rendered
 *   with the Section Rendering API, so what shoppers see always comes from the server.
 */
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const drawerEl = () => document.querySelector('toro-cart.toro-cart--drawer');
  const cartViews = () => [...document.querySelectorAll('.toro-cart[data-section-id]')];

  async function renderSections() {
    const ids = cartViews().map(v => v.dataset.sectionId);
    if (!ids.length) return;
    const res = await fetch(`${root}?sections=${ids.join(',')}`, { credentials: 'same-origin' });
    const data = await res.json();
    for (const view of cartViews()) {
      const html = data[view.dataset.sectionId];
      if (!html) continue;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-body]');
      const body = view.querySelector('[data-cart-body]');
      if (fresh && body) body.innerHTML = fresh.innerHTML;
      const freshCount = doc.querySelector('[data-cart-count-label]');
      view.querySelectorAll('[data-cart-count-label]').forEach(el => { if (freshCount) el.innerHTML = freshCount.innerHTML; });
    }
    updateBubble();
    syncUpsellArrows();
    await enforceLimits();
    document.dispatchEvent(new CustomEvent('toro:cart-rendered'));
  }

  // Per-customer limit (Theme settings > Limit per customer): if more got in than allowed, bring the line back to the cap.
  let enforcing = false;
  async function enforceLimits() {
    if (enforcing) return;
    const over = [...document.querySelectorAll('[data-cart-body] [data-max]')].filter(li => Number(li.dataset.qtyNow) > Number(li.dataset.max));
    if (!over.length) return;
    enforcing = true;
    const updates = {};
    over.forEach(li => { updates[li.dataset.key] = Number(li.dataset.max); });
    try { await post('cart/update.js', { updates }); } catch (e) {}
    await renderSections();
    enforcing = false;
  }
  document.addEventListener('DOMContentLoaded', enforceLimits);

  // Cart suggestion rows: arrows for mouse users, enabled only when there's more to scroll.
  function syncUpsellArrows(root = document) {
    root.querySelectorAll('.toro-upsell').forEach(box => {
      const list = box.querySelector('.toro-upsell__list');
      if (!list) return;
      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        box.classList.toggle('has-overflow', max > 4);
        const [prev, next] = box.querySelectorAll('[data-upsell-scroll]');
        if (prev) prev.disabled = list.scrollLeft <= 4;
        if (next) next.disabled = list.scrollLeft >= max - 4;
      };
      if (!list.dataset.arrows) {
        list.dataset.arrows = '1';
        list.addEventListener('scroll', update, { passive: true });
      }
      update();
    });
  }
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-upsell-scroll]');
    if (!b) return;
    const list = b.closest('.toro-upsell')?.querySelector('.toro-upsell__list');
    if (list) list.scrollBy({ left: Number(b.dataset.upsellScroll) * list.clientWidth * 0.8, behavior: 'smooth' });
  });
  window.addEventListener('resize', () => syncUpsellArrows());
  document.addEventListener('DOMContentLoaded', () => syncUpsellArrows());

  function updateBubble() {
    const inner = document.querySelector('[data-cart-body] [data-cart-count]');
    if (!inner) return;
    const count = Number(inner.dataset.cartCount) || 0;
    document.querySelectorAll('.cart-bubble').forEach(b => b.classList.toggle('visually-hidden', count === 0));
    document.querySelectorAll('.cart-bubble__text-count').forEach(t => {
      t.textContent = count > 99 ? '' : String(count);
      t.classList.toggle('hidden', count === 0);
    });
  }

  async function post(url, body) {
    const res = await fetch(`${root}${url}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.description || json.message || 'Something went wrong');
    return json;
  }

  class ToroCart extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', e => this.onClick(e));
      this.addEventListener('change', e => this.onChange(e));
      this.addEventListener('submit', e => this.onSubmit(e));
      this.addEventListener('keydown', e => { if (e.key === 'Escape' && this.classList.contains('toro-cart--drawer')) this.close(); });
    }
    open() {
      if (!this.classList.contains('toro-cart--drawer')) return;
      this.lastFocus = document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => { this.classList.add('is-open'); setTimeout(() => syncUpsellArrows(this), 320); });
      document.documentElement.classList.add('toro-cart-open');
      setTimeout(() => this.querySelector('.toro-cart__panel')?.focus(), 50);
    }
    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('toro-cart-open');
      setTimeout(() => { this.hidden = true; }, 280);
      this.lastFocus?.focus?.();
    }
    busy(on) { this.classList.toggle('is-busy', on); }
    lineOf(el) { return Number(el.closest('[data-line]')?.dataset.line); }
    async change(line, quantity) {
      const max = Number(this.querySelector(`[data-line="${line}"]`)?.dataset.max);
      if (max && quantity > max) quantity = max;
      this.busy(true);
      let message = '';
      try { await post('cart/change.js', { line, quantity }); } catch (e) { message = e.message; }
      await renderSections();
      this.busy(false);
      if (message) {
        const row = this.querySelector(`[data-line="${line}"] .toro-cart__info`);
        if (row) {
          const note = document.createElement('p');
          note.className = 'toro-cart__line-error';
          note.setAttribute('role', 'alert');
          note.textContent = message;
          row.appendChild(note);
        }
      }
    }
    codes() { return [...this.querySelectorAll('[data-remove-code]')].map(b => b.dataset.removeCode); }
    async setCodes(codes, added) {
      this.busy(true);
      let message = '';
      try {
        const cart = await post('cart/update.js', { discount: codes.join(',') });
        if (added) {
          const found = (cart.discount_codes || []).find(d => d.code.toLowerCase() === added.toLowerCase());
          if (!found || found.applicable === false) {
            message = this.dataset.invalidCode || 'This code is not valid.';
            // Keep the other codes; drop the one that failed.
            await post('cart/update.js', { discount: codes.filter(c => c !== added).join(',') });
          }
        }
      } catch (e) { message = e.message; }
      await renderSections();
      this.busy(false);
      if (message) {
        const err = this.querySelector('[data-code-error]');
        const input = this.querySelector('[data-code-input]');
        if (input && added) input.value = added;
        if (err) { err.textContent = message; err.hidden = false; }
      }
    }
    onClick(e) {
      const t = e.target;
      if (t.closest('[data-cart-close]')) { e.preventDefault(); this.close(); return; }
      const minus = t.closest('[data-qty-minus]'), plus = t.closest('[data-qty-plus]');
      if (minus || plus) {
        const input = t.closest('[data-qty]').querySelector('[data-qty-input]');
        const q = Math.max(0, Number(input.value) + (plus ? 1 : -1));
        input.value = q;
        this.change(this.lineOf(t), q);
        return;
      }
      if (t.closest('[data-remove]')) { this.change(this.lineOf(t), 0); return; }
      const rm = t.closest('[data-remove-code]');
      if (rm) this.setCodes(this.codes().filter(c => c !== rm.dataset.removeCode));
    }
    onChange(e) {
      const input = e.target.closest('[data-qty-input]');
      if (input) this.change(this.lineOf(input), Math.max(0, Number(input.value) || 0));
    }
    onSubmit(e) {
      const form = e.target.closest('[data-code-form]');
      if (!form) return;
      e.preventDefault();
      const input = form.querySelector('[data-code-input]');
      const code = input.value.trim();
      if (!code) return;
      this.setCodes([...new Set([...this.codes(), code])], code);
    }
  }
  if (!customElements.get('toro-cart')) customElements.define('toro-cart', ToroCart);

  // Header cart icon opens the drawer (the link still goes to /cart without JavaScript).
  document.addEventListener('click', e => {
    const link = e.target.closest('a.action__cart, a[href="/cart"].header-actions__action');
    const drawer = drawerEl();
    if (!link || !drawer || window.location.pathname === '/cart') return;
    e.preventDefault();
    drawer.open();
  });

  // Quick add buttons on product cards.
  document.addEventListener('submit', async e => {
    const form = e.target.closest('form[data-toro-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button[type=submit]');
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('is-loading');
    try {
      const q = form.querySelector('[name=quantity]:checked, input[name=quantity]:not([type=radio])');
      await post('cart/add.js', { items: [{ id: Number(form.dataset.variantId), quantity: Math.max(1, Number(q && q.value) || 1) }] });
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.innerHTML = form.dataset.addedLabel || 'Added';
      await renderSections();
      drawerEl()?.open();
    } catch (err) {
      btn.innerHTML = err.message.length < 40 ? err.message : (form.dataset.errorLabel || 'Unavailable');
    } finally {
      setTimeout(() => { btn.disabled = false; btn.classList.remove('is-loading', 'is-added'); btn.innerHTML = label; }, 1800);
    }
  });

  // Lets other features (e.g. the welcome popup applying a code) re-render the cart.
  window.ToroCart = { refresh: renderSections };

  // "Get your welcome discount" link under the code box: opens the welcome popup; hidden once they've signed up.
  const syncWelcome = () => {
    let done = false; try { done = !!localStorage.getItem('toro_signed_up'); } catch (e) {}
    document.querySelectorAll('[data-welcome-offer]').forEach(el => { el.hidden = done || !window.ToroPopup; });
  };
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-welcome-offer]');
    if (b && window.ToroPopup) { e.preventDefault(); window.ToroPopup.open(); }
  });
  document.addEventListener('toro:signed-up', syncWelcome);
  document.addEventListener('toro:cart-rendered', syncWelcome);
  document.addEventListener('DOMContentLoaded', () => setTimeout(syncWelcome, 300));

  // Anything else that adds to the cart (e.g. the product page form): refresh and open the drawer afterwards.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    try {
      const url = String(args[0]?.url || args[0] || '');
      const method = String(args[1]?.method || args[0]?.method || 'GET').toUpperCase();
      const fromToro = args[1]?.headers && JSON.stringify(args[1].headers).includes('application/json') && url.includes('cart/add.js');
      if (method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url) && response.ok && !fromToro) {
        setTimeout(async () => { await renderSections(); drawerEl()?.open(); }, 150);
      }
    } catch (e) {}
    return response;
  };
})();
